import { useCallback } from 'react';
import { useState } from 'react';
import { useNodesState, useEdgesState, addEdge } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import { NODE_TYPES } from './nodeTypes';

/**
 * Workflow data layer shared by Canvas Mode and Simple Mode.
 *
 * Owns: nodes, edges, nodeConfigs, nodeOutputs, workflowName.
 * Exposes raw state + low-level mutations. View-specific concerns
 * (panning, context menus, drag/drop, fitView) live in the view
 * components, not here.
 */
export default function useWorkflowState(workflow, { defaultEdgeOptions } = {}) {
  const initialDef = workflow.definition || {};
  const [nodes, setNodes, onNodesChange] = useNodesState(
    (initialDef.nodes || []).map((n) => ({ ...n, type: 'workflowNode' }))
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialDef.edges || []);
  const [nodeConfigs, setNodeConfigs] = useState(initialDef.nodeConfigs || {});
  const [nodeOutputs, setNodeOutputs] = useState(initialDef.nodeOutputs || {});
  const [workflowName, setWorkflowName] = useState(workflow.name);

  // ------- High-level mutations -------

  /**
   * Insert a new node at a flow position. Optionally connects an edge from
   * an existing source (used by drop, context-menu add, and placeholder-add).
   * Returns the new node id.
   */
  const addNodeAt = useCallback(
    ({ typeKey, position, connectFrom = null }) => {
      const nodeDef = NODE_TYPES[typeKey];
      if (!nodeDef) return null;

      const nodeId = `${typeKey}_${uuidv4().slice(0, 8)}`;
      const newNode = {
        id: nodeId,
        type: 'workflowNode',
        position,
        data: { nodeType: typeKey, label: nodeDef.label },
      };

      setNodes((nds) => [...nds, newNode]);
      setNodeConfigs((prev) => ({
        ...prev,
        [nodeId]: { ...nodeDef.defaultConfig },
      }));

      if (connectFrom) {
        const { nodeId: sourceNodeId, handleId, handleType } = connectFrom;
        const edgeParams =
          handleType === 'target'
            ? {
                source: nodeId,
                target: sourceNodeId,
                targetHandle: handleId || undefined,
              }
            : {
                source: sourceNodeId,
                sourceHandle: handleId || undefined,
                target: nodeId,
              };
        setEdges((eds) =>
          addEdge({ ...edgeParams, ...(defaultEdgeOptions || {}) }, eds)
        );
      }

      return nodeId;
    },
    [setNodes, setEdges, defaultEdgeOptions]
  );

  const deleteNode = useCallback(
    (nodeId) => {
      if (!nodeId) return;
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId)
      );
      setNodeConfigs((prev) => {
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });
      setNodeOutputs((prev) => {
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });
    },
    [setNodes, setEdges]
  );

  const updateNodeConfig = useCallback(
    (nodeId, newConfig) => {
      if (!nodeId) return;
      setNodeConfigs((prev) => ({ ...prev, [nodeId]: newConfig }));
    },
    []
  );

  const setNodeOutput = useCallback((nodeId, data, schema) => {
    setNodeOutputs((prev) => ({ ...prev, [nodeId]: { data, schema } }));
  }, []);

  const mergeNodeOutputSchema = useCallback((nodeId, schema) => {
    if (!nodeId) return;
    setNodeOutputs((prev) => ({
      ...prev,
      [nodeId]: { ...(prev[nodeId] || {}), schema },
    }));
  }, []);

  /**
   * Replace the entire workflow with a template (trigger + sequential
   * actions). Used by EmptyCanvasOverlay's "Use template" affordance.
   * Returns { triggerId } so the caller can fitView/select the new node.
   */
  const replaceWithTemplate = useCallback(
    (template) => {
      const triggerDef = NODE_TYPES[template.trigger];
      if (!triggerDef) return null;

      const triggerId = `${template.trigger}_${uuidv4().slice(0, 8)}`;
      const startX = 100;
      const startY = 200;
      const gapX = 250;

      const newNodes = [
        {
          id: triggerId,
          type: 'workflowNode',
          position: { x: startX, y: startY },
          data: { nodeType: template.trigger, label: triggerDef.label },
        },
      ];
      const newEdges = [];
      const newConfigs = { [triggerId]: { ...triggerDef.defaultConfig } };

      let prevId = triggerId;
      template.actions.forEach((actionType, idx) => {
        const actionDef = NODE_TYPES[actionType];
        if (!actionDef) return;
        const actionId = `${actionType}_${uuidv4().slice(0, 8)}`;
        newNodes.push({
          id: actionId,
          type: 'workflowNode',
          position: { x: startX + gapX * (idx + 1), y: startY },
          data: { nodeType: actionType, label: actionDef.label },
        });
        newEdges.push({
          id: `e_${prevId}_${actionId}`,
          source: prevId,
          target: actionId,
          ...(defaultEdgeOptions || {}),
        });
        newConfigs[actionId] = { ...actionDef.defaultConfig };
        prevId = actionId;
      });

      setNodes(newNodes);
      setEdges(newEdges);
      setNodeConfigs(newConfigs);

      return { triggerId };
    },
    [setNodes, setEdges, defaultEdgeOptions]
  );

  /**
   * Replace the workflow with a single trigger at the given position.
   * Used by EmptyCanvasOverlay's trigger picker.
   */
  const replaceWithSingleTrigger = useCallback(
    (triggerType, position) => {
      const triggerDef = NODE_TYPES[triggerType];
      if (!triggerDef) return null;
      const triggerId = `${triggerType}_${uuidv4().slice(0, 8)}`;
      setNodes([
        {
          id: triggerId,
          type: 'workflowNode',
          position,
          data: { nodeType: triggerType, label: triggerDef.label },
        },
      ]);
      setNodeConfigs({ [triggerId]: { ...triggerDef.defaultConfig } });
      return { triggerId };
    },
    [setNodes]
  );

  /**
   * Append an edge to the workflow (from an interactive connect).
   */
  const connectNodes = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge({ ...params, ...(defaultEdgeOptions || {}) }, eds)
      );
    },
    [setEdges, defaultEdgeOptions]
  );

  /**
   * Insert a new node between two existing connected nodes. Removes the
   * direct prevId → nextId edge (matching the optional sourceHandle) and
   * inserts: prevId → newId → nextId.
   *
   * Used by Simple Mode's "+ Add a step" buttons. Returns the new node id.
   */
  const insertNodeBetween = useCallback(
    ({ prevId, nextId, typeKey, sourceHandle = null }) => {
      const nodeDef = NODE_TYPES[typeKey];
      if (!nodeDef) return null;

      const newId = `${typeKey}_${uuidv4().slice(0, 8)}`;

      // Compute a position roughly between the two existing nodes so a
      // user toggling back to Canvas Mode sees something sensible.
      let position = { x: 0, y: 0 };
      setNodes((nds) => {
        const prev = nds.find((n) => n.id === prevId);
        const next = nds.find((n) => n.id === nextId);
        if (prev && next) {
          position = {
            x: (prev.position.x + next.position.x) / 2,
            y: (prev.position.y + next.position.y) / 2,
          };
        } else if (prev) {
          position = { x: prev.position.x + 250, y: prev.position.y };
        }
        return [
          ...nds,
          {
            id: newId,
            type: 'workflowNode',
            position,
            data: { nodeType: typeKey, label: nodeDef.label },
          },
        ];
      });

      setNodeConfigs((prev) => ({
        ...prev,
        [newId]: { ...nodeDef.defaultConfig },
      }));

      setEdges((eds) => {
        // Drop the original prev → next edge that matches the source handle.
        const filtered = eds.filter(
          (e) =>
            !(
              e.source === prevId &&
              e.target === nextId &&
              (e.sourceHandle || null) === (sourceHandle || null)
            )
        );
        const baseStyle = defaultEdgeOptions || {};
        const incoming = {
          id: `e_${prevId}_${newId}_${sourceHandle || ''}`,
          source: prevId,
          target: newId,
          ...(sourceHandle ? { sourceHandle } : {}),
          ...baseStyle,
        };
        const outgoing = {
          id: `e_${newId}_${nextId}`,
          source: newId,
          target: nextId,
          ...baseStyle,
        };
        return [...filtered, incoming, outgoing];
      });

      return newId;
    },
    [setNodes, setNodeConfigs, setEdges, defaultEdgeOptions]
  );

  /**
   * Append a new node after an existing node, connected via prev → new
   * (with optional source handle). Used by Simple Mode's end-of-chain
   * "+ Add a step" button. Returns the new node id.
   */
  const appendAfter = useCallback(
    ({ prevId, typeKey, sourceHandle = null }) => {
      const nodeDef = NODE_TYPES[typeKey];
      if (!nodeDef) return null;
      const newId = `${typeKey}_${uuidv4().slice(0, 8)}`;
      let position = { x: 250, y: 200 };
      setNodes((nds) => {
        const prev = nds.find((n) => n.id === prevId);
        if (prev) position = { x: prev.position.x + 250, y: prev.position.y };
        return [
          ...nds,
          {
            id: newId,
            type: 'workflowNode',
            position,
            data: { nodeType: typeKey, label: nodeDef.label },
          },
        ];
      });
      setNodeConfigs((prev) => ({
        ...prev,
        [newId]: { ...nodeDef.defaultConfig },
      }));
      setEdges((eds) =>
        addEdge(
          {
            source: prevId,
            target: newId,
            ...(sourceHandle ? { sourceHandle } : {}),
            ...(defaultEdgeOptions || {}),
          },
          eds
        )
      );
      return newId;
    },
    [setNodes, setNodeConfigs, setEdges, defaultEdgeOptions]
  );

  // ------- Persistence -------

  const serialize = useCallback(
    () => ({
      nodes: nodes.map(({ id, position, data }) => ({ id, position, data })),
      edges,
      nodeConfigs,
      nodeOutputs,
    }),
    [nodes, edges, nodeConfigs, nodeOutputs]
  );

  return {
    // State
    nodes,
    edges,
    nodeConfigs,
    nodeOutputs,
    workflowName,
    // Setters (raw, for xyflow + plain text fields)
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    setWorkflowName,
    // Mutations
    addNodeAt,
    deleteNode,
    updateNodeConfig,
    setNodeOutput,
    mergeNodeOutputSchema,
    replaceWithTemplate,
    replaceWithSingleTrigger,
    connectNodes,
    insertNodeBetween,
    appendAfter,
    // Persistence
    serialize,
  };
}
